/**
 * Brand URL Configuration for Verify UI
 *
 * Provides hostname-based brand detection and URL resolution for multi-brand support.
 * The verify-ui auto-detects the brand based on the current page hostname and returns
 * the appropriate service URLs.
 */

/**
 * Brand-specific URLs for a given environment
 */
export interface BrandUrls {
  apiUrl: string;
  engineUrl: string;
  wsUrl: string;
  portalUrl: string;
}

const TRUSTED_VERIFY_HOSTS = {
  'av.safepassageapp.com': {
    brand: 'safepassage',
    environment: 'production',
    baseDomain: 'safepassageapp.com',
  },
  'verify.staging.safepassageapp.com': {
    brand: 'safepassage',
    environment: 'staging',
    baseDomain: 'staging.safepassageapp.com',
  },
  'verify.privateav.com': {
    brand: 'privateav',
    environment: 'production',
    baseDomain: 'privateav.com',
  },
  'verify.staging.privateav.com': {
    brand: 'privateav',
    environment: 'staging',
    baseDomain: 'staging.privateav.com',
  },
} as const;

type TrustedVerifyHostname = keyof typeof TRUSTED_VERIFY_HOSTS;

function trustedVerifyHost(hostname: string) {
  return TRUSTED_VERIFY_HOSTS[hostname.toLowerCase() as TrustedVerifyHostname];
}

export function detectEnvironmentFromHostname(
  hostname: string
): 'development' | 'staging' | 'production' {
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'development';
  }

  const trustedHost = trustedVerifyHost(hostname);
  if (!trustedHost) {
    throw new Error('Untrusted verification hostname');
  }

  return trustedHost.environment;
}

/**
 * Detect brand from hostname
 *
 * Analyzes the provided hostname to determine which brand's URLs to use.
 * Returns null if brand cannot be determined - caller must handle this case.
 *
 * IMPORTANT: No default brand - both brands are equal and independent.
 *
 * @param hostname - The hostname to analyze
 * @returns The detected brand slug ('safepassage' or 'privateav') or null if unknown
 */
export function detectBrandFromHostname(hostname: string): string | null {
  return trustedVerifyHost(hostname)?.brand || null;
}

/**
 * Build the fixed service set associated with a trusted verification hostname.
 */
function urlsForBaseDomain(baseDomain: string): BrandUrls {
  const protocol = 'https';
  const wsProtocol = 'wss';

  return {
    apiUrl: `${protocol}://api.${baseDomain}`,
    engineUrl: `${protocol}://engine.${baseDomain}`,
    wsUrl: `${wsProtocol}://engine.${baseDomain}/api/websocket/stream`,
    portalUrl: `${protocol}://portal.${baseDomain}/dashboard/billing`,
  };
}

/**
 * Get brand URLs for the specified environment
 *
 * Returns the fixed service URLs for the exact verification hostname.
 *
 * @param environment - The environment ('production', 'staging', or 'development')
 * @param brandOverride - Optional brand slug to use instead of auto-detection
 * @returns Brand-specific URLs for the environment
 */
export function getBrandUrls(
  environment: 'production' | 'staging' | 'development'
): BrandUrls {
  const hostname =
    typeof window !== 'undefined' && window.location
      ? window.location.hostname
      : '';

  // In development, use localhost URLs
  if (environment === 'development') {
    return {
      apiUrl: 'http://localhost:3001',
      engineUrl: 'http://localhost:8000',
      wsUrl: 'ws://localhost:8000/api/websocket/stream',
      portalUrl: 'http://localhost:3002/dashboard/billing',
    };
  }

  const trustedHost = trustedVerifyHost(hostname);
  if (trustedHost) {
    if (environment !== trustedHost.environment) {
      throw new Error('Verification environment does not match the trusted hostname');
    }
    return urlsForBaseDomain(trustedHost.baseDomain);
  }

  throw new Error('Untrusted verification hostname');
}
